import { useParams } from "react-router-dom";
import { WSCollectionInstance } from "../../../services/remote/delivery/WSCollection";
import { useEffect, useState } from "react";
import { Collection } from "../../../entity/Collection";
import { Action } from "../../../entity/Action";
import { WSCollectionTypeInstance } from "../../../services/remote/delivery/WSCollectionType";
import { CollectionType } from "../../../entity/CollectionType";
import {
  HStack,
  VStack,
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Badge,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  useToast,
  Spinner,
  Grid,
  GridItem,
  Divider,
  Icon,
  useColorModeValue,
  ModalOverlay,
  Modal,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  FormControl,
  FormLabel,
  Textarea,
  ModalFooter,
} from "@chakra-ui/react";
import { SearchIcon, AddIcon, CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";

export const AddCollectionToAction = () => {
  const { t } = useTranslation();
  const companyId = useParams().companyId;
  const toast = useToast();

  const [collections, setCollections] = useState<Collection[]>([]);
  const [actions, setActions] = useState<Action[]>([]);
  const [collectionTypes, setCollectionTypes] = useState<CollectionType[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAction, setSelectedAction] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [isCreateActionOpen, setIsCreateActionOpen] = useState(false);
  const [newActionName, setNewActionName] = useState("");
  const [newActionDescription, setNewActionDescription] = useState("");
  const [isCreatingAction, setIsCreatingAction] = useState(false);

  // Color mode values
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const fetchCollections = async () => {
    try {
      const response = await WSCollectionInstance.getCollections(
        companyId || "",
      );
      setCollections(response);
    } catch (error) {
      console.error("Error fetching collections:", error);
      toast({
        title: t("addCollectionToAction.toast.errorFetchingCollections"),
        description: t("addCollectionToAction.toast.failedToLoadCollections"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchActions = async () => {
    try {
      const response = await WSCollectionTypeInstance.getActions();
      setActions(response);
    } catch (error) {
      console.error("Error fetching actions:", error);
      toast({
        title: t("addCollectionToAction.toast.errorFetchingActions"),
        description: t("addCollectionToAction.toast.failedToLoadActions"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchCollectionTypes = async () => {
    try {
      const response =
        await WSCollectionTypeInstance.getCollectionTypesByCompanyId(
          companyId || "",
        );
      setCollectionTypes(response);
    } catch (error) {
      console.error("Error fetching collection types:", error);
    }
  };

  const checkIfCollectionIsAlreadyAdded = (collection: Collection) => {
    return collectionTypes.some(
      (actionType) => actionType.collectionId === collection.id,
    );
  };

  const handleAddCollectionToAction = async (
    collection: Collection,
    actionId: string,
  ) => {
    if (!actionId) {
      toast({
        title: t("addCollectionToAction.toast.noActionSelected"),
        description: t("addCollectionToAction.toast.pleaseSelectAction"),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      await WSCollectionTypeInstance.createCollectionType(
        collection.id!,
        actionId,
        companyId!,
      );

      toast({
        title: t("addCollectionToAction.toast.success"),
        description: t("addCollectionToAction.toast.collectionAddedSuccess", {
          name: collection.name,
        }),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Refresh data
      fetchCollectionTypes();
    } catch (error) {
      console.error("Error adding collection to action:", error);
      toast({
        title: t("addCollectionToAction.toast.error"),
        description: t("addCollectionToAction.toast.failedToAddCollection"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAction = async () => {
    if (!newActionName.trim() || !newActionDescription.trim()) {
      toast({
        title: t("addCollectionToAction.createAction.validationError"),
        description: t("addCollectionToAction.createAction.fillAllFields"),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsCreatingAction(true);
    try {
      await WSCollectionTypeInstance.createAction(
        newActionName.trim(),
        newActionDescription.trim(),
      );

      toast({
        title: t("addCollectionToAction.createAction.success"),
        description: t("addCollectionToAction.createAction.actionCreated"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Reset form
      setNewActionName("");
      setNewActionDescription("");
      setIsCreateActionOpen(false);

      // Refresh actions list
      await fetchActions();
    } catch (error) {
      console.error("Error creating action:", error);
      toast({
        title: t("addCollectionToAction.createAction.error"),
        description: t("addCollectionToAction.createAction.failedToCreate"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCreatingAction(false);
    }
  };

  const filteredCollections = collections.filter(
    (collection) =>
      collection.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      collection.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      collection.path.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    fetchCollections();
    fetchActions();
    fetchCollectionTypes();
  }, [companyId]);

  return (
    <Box p={6} maxW="1400px" mx="auto">
      <Heading mb={6} size="lg" color="gray.800">
        {t("addCollectionToAction.title")}
      </Heading>

      {/* Search Bar */}
      <Box mb={6}>
        <InputGroup maxW="400px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder={t("addCollectionToAction.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            bg={cardBg}
            borderColor={borderColor}
          />
        </InputGroup>
      </Box>

      {/* Action Selector */}
      <Box mb={6}>
        <Text mb={2} fontWeight="semibold" color="gray.700">
          {t("addCollectionToAction.selectAction")}
        </Text>
        <HStack spacing={2} flexWrap="wrap">
          <Button
            size="sm"
            variant={!selectedAction ? "solid" : "outline"}
            colorScheme="blue"
            onClick={() => setSelectedAction("")}
          >
            {t("addCollectionToAction.allActions")}
          </Button>
          {actions.map((action) => (
            <Button
              key={action.id}
              size="sm"
              variant={selectedAction === action.id ? "solid" : "outline"}
              colorScheme="blue"
              onClick={() => setSelectedAction(action.id)}
            >
              {action.description}
            </Button>
          ))}
        </HStack>
      </Box>

      <Grid templateColumns="1fr 300px" gap={6}>
        {/* Collections Grid */}
        <GridItem>
          <Heading size="md" mb={4} color="gray.700">
            {t("addCollectionToAction.collections")} (
            {filteredCollections.length})
          </Heading>

          {filteredCollections.length === 0 ? (
            <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
              <CardBody textAlign="center" py={8}>
                <Text color="gray.500">
                  {searchTerm
                    ? t("addCollectionToAction.noCollectionsFound")
                    : t("addCollectionToAction.noCollectionsAvailable")}
                </Text>
              </CardBody>
            </Card>
          ) : (
            <Grid
              templateColumns="repeat(auto-fill, minmax(350px, 1fr))"
              gap={4}
            >
              {filteredCollections.map((collection) => {
                const isAdded = checkIfCollectionIsAlreadyAdded(collection);

                return (
                  <Card
                    key={collection.id}
                    bg={cardBg}
                    borderColor={borderColor}
                    borderWidth="1px"
                    _hover={{ shadow: "md", transform: "translateY(-2px)" }}
                    transition="all 0.2s"
                  >
                    <CardHeader pb={2}>
                      <HStack justify="space-between" align="start">
                        <Heading size="sm" noOfLines={1}>
                          {collection.name}
                        </Heading>
                        <Badge
                          colorScheme={isAdded ? "green" : "gray"}
                          variant="solid"
                          fontSize="xs"
                        >
                          {isAdded ? (
                            <>
                              <CheckIcon boxSize={3} mr={1} />
                              {t("addCollectionToAction.added")}
                            </>
                          ) : (
                            <>
                              <CloseIcon boxSize={3} mr={1} />
                              {t("addCollectionToAction.notAdded")}
                            </>
                          )}
                        </Badge>
                      </HStack>
                    </CardHeader>

                    <CardBody pt={0}>
                      <VStack align="start" spacing={2}>
                        <Text fontSize="sm" color="gray.600" noOfLines={2}>
                          {collection.description}
                        </Text>

                        <Text fontSize="xs" color="gray.500" fontFamily="mono">
                          {t("addCollectionToAction.path")}: {collection.path}
                        </Text>

                        <Text fontSize="xs" color="gray.500">
                          {t("addCollectionToAction.type")}: {collection.type}
                        </Text>

                        <Divider />

                        <Button
                          size="sm"
                          colorScheme="blue"
                          leftIcon={<AddIcon />}
                          onClick={() =>
                            handleAddCollectionToAction(
                              collection,
                              selectedAction,
                            )
                          }
                          isDisabled={isAdded || loading}
                          w="full"
                          variant={isAdded ? "outline" : "solid"}
                        >
                          {isAdded
                            ? t("addCollectionToAction.alreadyAdded")
                            : t("addCollectionToAction.addToAction")}
                        </Button>
                      </VStack>
                    </CardBody>
                  </Card>
                );
              })}
            </Grid>
          )}
        </GridItem>

        {/* Actions Sidebar */}
        <GridItem>
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardHeader>
              <HStack justify="space-between" align="center">
                <Box>
                  <Heading size="sm" color="gray.700">
                    {t("addCollectionToAction.availableActions")}
                  </Heading>
                  <Text fontSize="xs" color="gray.500">
                    {t("addCollectionToAction.selectActionDescription")}
                  </Text>
                </Box>
                <Button
                  size="sm"
                  colorScheme="green"
                  leftIcon={<AddIcon />}
                  onClick={() => setIsCreateActionOpen(true)}
                >
                  {t("addCollectionToAction.createAction.newAction")}
                </Button>
              </HStack>
            </CardHeader>
            <CardBody>
              <VStack align="start" spacing={3}>
                {actions.map((action) => (
                  <Box
                    key={action.id}
                    p={3}
                    bg={selectedAction === action.id ? "blue.100" : "gray.50"}
                    borderRadius="md"
                    w="full"
                    cursor="pointer"
                    onClick={() => setSelectedAction(action.id)}
                    _hover={{ bg: "blue.100" }}
                    transition="background-color 0.2s"
                  >
                    <Text fontWeight="medium" fontSize="sm">
                      {action.description}
                    </Text>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      {action.action}
                    </Text>
                  </Box>
                ))}
              </VStack>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>
      <Modal
        isOpen={isCreateActionOpen}
        onClose={() => setIsCreateActionOpen(false)}
        size="md"
      >
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader>
            {t("addCollectionToAction.createAction.title")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="medium">
                  {t("addCollectionToAction.createAction.actionName")}
                </FormLabel>
                <Input
                  value={newActionName}
                  onChange={(e) => setNewActionName(e.target.value)}
                  placeholder={t(
                    "addCollectionToAction.createAction.actionNamePlaceholder",
                  )}
                  bg={cardBg}
                  borderColor={borderColor}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="medium">
                  {t("addCollectionToAction.createAction.actionDescription")}
                </FormLabel>
                <Textarea
                  value={newActionDescription}
                  onChange={(e) => setNewActionDescription(e.target.value)}
                  placeholder={t(
                    "addCollectionToAction.createAction.actionDescriptionPlaceholder",
                  )}
                  bg={cardBg}
                  borderColor={borderColor}
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing={3}>
              <Button
                variant="outline"
                onClick={() => setIsCreateActionOpen(false)}
                isDisabled={isCreatingAction}
              >
                {t("addCollectionToAction.createAction.cancel")}
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleCreateAction}
                isLoading={isCreatingAction}
                loadingText={t("addCollectionToAction.createAction.creating")}
              >
                {t("addCollectionToAction.createAction.create")}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
